import type { PublicConfig, RenderStatusResponse, UploadRole } from '../../../shared/types';
import { fetchPublicConfig } from './api';
import {
  clearActiveRenderGroupId,
  getActiveRenderGroupId,
  getOrCreatePairGroupId,
  listUnfinishedSessions,
  resetPairGroupId,
  setActiveRenderGroupId,
} from './storage';
import { RenderTracker } from './RenderTracker';
import { UploadTask } from './UploadTask';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.mkv', '.webm', '.avi', '.m4v'];
const SUBTITLE_EXTENSIONS = ['.ass'];

export interface SlotValidation {
  error?: string;
}

function extensionOf(filename: string): string {
  return filename.includes('.') ? `.${filename.split('.').pop()!.toLowerCase()}` : '';
}

/**
 * Orchestrates exactly one video + one subtitle upload as a pair. Each slot
 * is an independent UploadTask (so the video can be uploading while the
 * user is still picking the subtitle), tied together by a shared groupId
 * the server uses to know when to start (and track) a render job for the
 * finished pair.
 */
export class PairUploader {
  private config: PublicConfig | null = null;
  private groupId: string;
  private message = '';
  private tasks: Partial<Record<UploadRole, UploadTask>> = {};
  private onChange: () => void;
  private autoPausedForOffline = new Set<UploadRole>();
  private renderTracker: RenderTracker | null = null;
  /**
   * Whether the persisted groupId has already been rotated out from under
   * the current (completed) pair. Guards against rotating repeatedly as
   * further progress events fire, and — more importantly — means the
   * rotation happens the instant both uploads finish rather than lazily on
   * the next setFile() call, which would otherwise never run if the page
   * gets reloaded (or the tab is simply reopened later) before the user
   * starts a second pair: a fresh PairUploader's `tasks` map starts empty,
   * so a since-forgotten in-memory-only completion check could never catch
   * a stale groupId still sitting in localStorage from the finished pair.
   */
  private pairFinalized = false;

  constructor(onChange: () => void) {
    this.onChange = onChange;
    this.groupId = getOrCreatePairGroupId();
    window.addEventListener('offline', () => this.handleOffline());
    window.addEventListener('online', () => this.handleOnline());

    // Resume showing progress for a render started before a reload (or in
    // a previous visit) — renders can run for hours, well past any single
    // page session.
    const activeRenderGroupId = getActiveRenderGroupId();
    if (activeRenderGroupId) {
      this.renderTracker = new RenderTracker(activeRenderGroupId, () => this.onChange());
    }
  }

  async init(): Promise<PublicConfig> {
    this.config = await fetchPublicConfig();
    return this.config;
  }

  private getConfig(): PublicConfig {
    if (!this.config) throw new Error('PairUploader not initialized');
    return this.config;
  }

  setMessage(message: string): void {
    this.message = message;
  }

  getTask(role: UploadRole): UploadTask | undefined {
    return this.tasks[role];
  }

  hasUnfinishedSessions(): boolean {
    return listUnfinishedSessions().length > 0;
  }

  unfinishedSessionSummaries(): { filename: string; fileSize: number }[] {
    return listUnfinishedSessions().map((s) => ({ filename: s.filename, fileSize: s.fileSize }));
  }

  validateFile(role: UploadRole, file: File): SlotValidation {
    const config = this.getConfig();
    if (file.size > config.maxFileSize) {
      return { error: 'The selected file exceeds the maximum allowed size.' };
    }
    const ext = extensionOf(file.name);
    if (role === 'video') {
      const looksLikeVideo = file.type.startsWith('video/') || VIDEO_EXTENSIONS.includes(ext);
      if (!looksLikeVideo) return { error: 'Please choose a video file.' };
    } else {
      if (!SUBTITLE_EXTENSIONS.includes(ext)) return { error: 'Please choose a .ass subtitle file.' };
    }
    return {};
  }

  setFile(role: UploadRole, file: File): SlotValidation {
    const validation = this.validateFile(role, file);
    if (validation.error) return validation;

    if (this.pairFinalized) {
      // handleTaskUpdate() already rotated the groupId out from under this
      // now-finished pair the instant it completed; just clear the leftover
      // completed cards (and any render progress being shown for them)
      // before starting the new one.
      this.tasks = {};
      this.pairFinalized = false;
      this.renderTracker?.stop();
      this.renderTracker = null;
      clearActiveRenderGroupId();
    } else {
      const existing = this.tasks[role];
      if (existing) void existing.cancel();
    }

    const task = new UploadTask(file, {
      message: this.message || undefined,
      groupId: this.groupId,
      role,
      config: this.getConfig(),
      onUpdate: () => this.handleTaskUpdate(),
    });
    this.tasks[role] = task;
    this.onChange();
    void task.start();
    return {};
  }

  private handleTaskUpdate(): void {
    // The moment both uploads are confirmed complete, immediately rotate
    // the persisted groupId — don't wait for the user to start a new pair.
    // A page reload before that point would otherwise start a fresh
    // PairUploader whose in-memory `tasks` map can no longer tell that the
    // groupId sitting in localStorage already belongs to a finished pair.
    if (!this.pairFinalized && this.bothCompleted()) {
      this.pairFinalized = true;
      const finishedGroupId = this.groupId;
      this.groupId = resetPairGroupId();

      this.renderTracker?.stop();
      setActiveRenderGroupId(finishedGroupId);
      this.renderTracker = new RenderTracker(finishedGroupId, () => this.onChange());
    }
    this.onChange();
  }

  pause(role: UploadRole): void {
    this.tasks[role]?.pause();
  }

  resume(role: UploadRole): void {
    this.tasks[role]?.resume();
  }

  async remove(role: UploadRole): Promise<void> {
    const task = this.tasks[role];
    if (!task) return;
    await task.cancel();
    delete this.tasks[role];
    this.onChange();
  }

  bothCompleted(): boolean {
    return (
      this.tasks.video?.getSnapshot().status === 'completed' &&
      this.tasks.subtitle?.getSnapshot().status === 'completed'
    );
  }

  getRenderStatus(): RenderStatusResponse | null {
    return this.renderTracker?.getStatus() ?? null;
  }

  retryRender(): void {
    void this.renderTracker?.retry();
  }

  /** Clears both slots and starts a fresh pairing group for the next upload. */
  startNewPair(): void {
    if (!this.pairFinalized) this.groupId = resetPairGroupId();
    this.pairFinalized = false;
    this.tasks = {};
    this.renderTracker?.stop();
    this.renderTracker = null;
    clearActiveRenderGroupId();
    this.onChange();
  }

  private handleOffline(): void {
    for (const role of ['video', 'subtitle'] as const) {
      const task = this.tasks[role];
      if (!task) continue;
      const status = task.getSnapshot().status;
      if (status === 'uploading' || status === 'retrying') {
        this.autoPausedForOffline.add(role);
        task.pause();
      }
    }
  }

  private handleOnline(): void {
    for (const role of this.autoPausedForOffline) {
      this.tasks[role]?.resume();
    }
    this.autoPausedForOffline.clear();
  }
}
